import { getVideoSource, getDigitalMediaVideoCode, getEmbedUrl, getUnsupportedUrlMessage, isSupportedVideoSource } from './video-utils';

function clear(node) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

const renderState = new WeakMap();

function beginRender(container) {
  const prev = renderState.get(container);
  if (prev?.controller) {
    try {
      prev.controller.abort();
    } catch {}
  }

  const next = {
    seq: (prev?.seq || 0) + 1,
    controller: new AbortController(),
  };

  renderState.set(container, next);
  return next;
}

async function buildMediaNode(rawUrl, isUpload, { signal } = {}) {
  if (!rawUrl) return null;

  if (isUpload) {
    const video = document.createElement('video');
    video.className = 'snl-video-playlist__media';
    video.controls = true;
    video.src = rawUrl;
    return video;
  }

  const source = getVideoSource(rawUrl);
  if (source === 'digitalmedia' && !isSupportedVideoSource(source)) {
    const ph = document.createElement('div');
    ph.className = 'snl-video-playlist__placeholder';
    ph.textContent = getUnsupportedUrlMessage('digitalmedia');
    return ph;
  }

  const iframe = document.createElement('iframe');
  iframe.className = 'snl-video-playlist__media';
  iframe.allowFullscreen = true;
  iframe.setAttribute('msallowfullscreen', 'true');
  iframe.setAttribute('title', 'Playlist video');
  iframe.loading = 'lazy';

  const videoCode = source === 'digitalmedia' ? getDigitalMediaVideoCode(rawUrl) : '';

  try {
    const embed = await getEmbedUrl(rawUrl, videoCode, { signal });
    if (signal?.aborted) return null;

    if (source === 'digitalmedia' && !embed) {
      const ph = document.createElement('div');
      ph.className = 'snl-video-playlist__placeholder';
      ph.textContent = getUnsupportedUrlMessage('digitalmedia');
      return ph;
    }

    iframe.src = embed || rawUrl;
  } catch (e) {
    if (e?.name === 'AbortError' || signal?.aborted) return null;
    console.error(e);
    iframe.src = rawUrl;
  }

  return iframe;
}

async function renderIntoContainer(container, rawUrl, isUpload, { placeholderText = '' } = {}) {
  const { seq, controller } = beginRender(container);

  clear(container);

  if (!rawUrl) {
    if (placeholderText) {
      const ph = document.createElement('div');
      ph.className = 'snl-video-playlist__placeholder';
      ph.textContent = placeholderText;
      container.appendChild(ph);
    }
    return;
  }

  try {
    const node = await buildMediaNode(rawUrl, isUpload, { signal: controller.signal });
    const cur = renderState.get(container);

    if (!cur || cur.seq !== seq || controller.signal.aborted) return;

    if (node) container.appendChild(node);
  } catch (e) {
    if (e?.name === 'AbortError' || controller.signal.aborted) return;
    console.error(e);
  }
}

function scrollPlayerIntoView(activeArea) {
  if (!activeArea) return;

  try {
    activeArea.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch {
    activeArea.scrollIntoView(true);
  }
}

function initHeroMode(root) {
  const activeArea = root.querySelector('[data-active-player="1"] .snl-video-playlist__player');
  const tiles = Array.from(root.querySelectorAll('.snl-video-playlist-item__tile'));

  if (!activeArea || tiles.length === 0) return;

  activeArea.style.width = '100%';

  let currentIdx = 0;

  function syncRovingTabIndex(activeIdx) {
    tiles.forEach((t, i) => t.setAttribute('tabindex', i === activeIdx ? '0' : '-1'));
  }

  function focusTile(idx) {
    const t = tiles[idx];
    if (t && typeof t.focus === 'function') t.focus();
  }

  function setActive(idx, { focus = false, scrollToPlayer = false } = {}) {
    currentIdx = idx;

    tiles.forEach((t, i) => {
      const isActive = i === idx;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    syncRovingTabIndex(idx);

    const tile = tiles[idx];
    const rawUrl = tile.getAttribute('data-raw-url') || '';
    const isUpload = tile.getAttribute('data-is-upload') === '1';

    renderIntoContainer(activeArea, rawUrl, isUpload, { placeholderText: 'Select a video…' });

    if (scrollToPlayer) {
      scrollPlayerIntoView(activeArea);
    }

    if (focus) focusTile(idx);
  }

  function wrapIndex(idx) {
    if (idx < 0) return tiles.length - 1;
    if (idx >= tiles.length) return 0;
    return idx;
  }

  function handleKeydown(e, idx) {
    const key = e.key;

    const isLeft = key === 'ArrowLeft';
    const isRight = key === 'ArrowRight';
    const isHome = key === 'Home';
    const isEnd = key === 'End';
    const isActivate = key === 'Enter' || key === ' ' || key === 'Spacebar';

    if (!(isLeft || isRight || isHome || isEnd || isActivate)) return;

    e.preventDefault();
    e.stopPropagation();

    if (isActivate) {
      setActive(idx, { focus: true, scrollToPlayer: true });
      return;
    }

    let nextIdx = idx;
    if (isLeft) nextIdx = wrapIndex(idx - 1);
    if (isRight) nextIdx = wrapIndex(idx + 1);
    if (isHome) nextIdx = 0;
    if (isEnd) nextIdx = tiles.length - 1;

    syncRovingTabIndex(nextIdx);
    focusTile(nextIdx);
  }

  tiles.forEach((tile, idx) => {
    tile.setAttribute('role', 'button');
    tile.setAttribute('aria-pressed', 'false');

    tile.addEventListener('click', (e) => {
      e.preventDefault();
      setActive(idx, { focus: true, scrollToPlayer: true });
    });

    tile.addEventListener('keydown', (e) => handleKeydown(e, idx));
  });

  setActive(currentIdx, { focus: false, scrollToPlayer: false });
}

function initTilePlayMode(root) {
  const tiles = Array.from(root.querySelectorAll('.snl-video-playlist-item__tile'));
  if (tiles.length === 0) return;

  const originalThumbHTML = new Map();

  function getThumbEl(tile) {
    return tile.querySelector('.snl-video-playlist-item__thumb');
  }

  function deactivateAll(exceptIdx = -1) {
    tiles.forEach((t, i) => {
      const thumb = getThumbEl(t);
      if (!thumb) return;

      t.classList.toggle('is-active', i === exceptIdx);
      t.setAttribute('aria-pressed', i === exceptIdx ? 'true' : 'false');

      if (i !== exceptIdx) {
        const saved = originalThumbHTML.get(t);
        if (saved != null) {
          beginRender(thumb);
          thumb.innerHTML = saved;
        }
      }
    });
  }

  function syncRovingTabIndex(activeIdx) {
    tiles.forEach((t, i) => t.setAttribute('tabindex', i === activeIdx ? '0' : '-1'));
  }

  function focusTile(idx) {
    const t = tiles[idx];
    if (t && typeof t.focus === 'function') t.focus();
  }

  async function activateTile(idx, { focus = false } = {}) {
    const tile = tiles[idx];
    if (!tile) return;

    const thumb = getThumbEl(tile);
    if (!thumb) return;

    if (!originalThumbHTML.has(tile)) {
      originalThumbHTML.set(tile, thumb.innerHTML);
    }

    deactivateAll(idx);
    syncRovingTabIndex(idx);

    const rawUrl = tile.getAttribute('data-raw-url') || '';
    const isUpload = tile.getAttribute('data-is-upload') === '1';

    await renderIntoContainer(thumb, rawUrl, isUpload);

    if (focus) focusTile(idx);
  }

  function wrapIndex(idx) {
    if (idx < 0) return tiles.length - 1;
    if (idx >= tiles.length) return 0;
    return idx;
  }

  function handleKeydown(e, idx) {
    const key = e.key;

    const isLeft = key === 'ArrowLeft';
    const isRight = key === 'ArrowRight';
    const isHome = key === 'Home';
    const isEnd = key === 'End';
    const isActivate = key === 'Enter' || key === ' ' || key === 'Spacebar';

    if (!(isLeft || isRight || isHome || isEnd || isActivate)) return;

    e.preventDefault();
    e.stopPropagation();

    if (isActivate) {
      activateTile(idx, { focus: true }).catch(console.error);
      return;
    }

    let nextIdx = idx;
    if (isLeft) nextIdx = wrapIndex(idx - 1);
    if (isRight) nextIdx = wrapIndex(idx + 1);
    if (isHome) nextIdx = 0;
    if (isEnd) nextIdx = tiles.length - 1;

    syncRovingTabIndex(nextIdx);
    focusTile(nextIdx);
  }

  tiles.forEach((tile, idx) => {
    tile.setAttribute('role', 'button');
    tile.setAttribute('aria-pressed', 'false');
    tile.setAttribute('tabindex', idx === 0 ? '0' : '-1');

    tile.addEventListener('click', (e) => {
      e.preventDefault();
      activateTile(idx, { focus: true }).catch(console.error);
    });

    tile.addEventListener('keydown', (e) => handleKeydown(e, idx));
  });
}

function initOnePlaylist(root) {
  const hideHero = root.getAttribute('data-hide-hero') === '1';
  if (hideHero) initTilePlayMode(root);
  else initHeroMode(root);
}

function init() {
  document.querySelectorAll('.wp-block-snl-video-playlist').forEach(initOnePlaylist);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
